// src/firebase/config.js
import { initializeApp, getApps } from 'firebase/app';
import { initializeAppCheck, ReCaptchaV3Provider } from 'firebase/app-check';
import { getAuth } from 'firebase/auth';
import {
  getFirestore,
  connectFirestoreEmulator,
  enableIndexedDbPersistence,
} from 'firebase/firestore';
// Analytics is deliberately NOT initialized here: context/FirebaseContext.js
// starts it only for visitors who haven't opted out (Privacy Policy page), so
// an opted-out device never loads gtag.js at all.

// Firebase configuration
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
  databaseURL: process.env.REACT_APP_FIREBASE_DATABASE_URL,
};

// Initialize Firebase if not already initialized
let firebaseApp;
if (!getApps().length) {
  firebaseApp = initializeApp(firebaseConfig);
} else {
  firebaseApp = getApps()[0];
}

// ---------------------------------------------------------------- App Check
//
// Attests that requests come from this actual app rather than a script. Once
// initialized, the Firebase SDKs (Auth, Firestore) attach an App Check token
// automatically; our own REST API is a plain HTTP backend, so api.js attaches
// the token to those calls by hand via getAppCheckToken() below.
//
// Must run immediately after initializeApp and before anything issues a
// request, which is why it lives here rather than in a component.
//
// Deliberately conditional: with no site key configured this is skipped
// entirely rather than throwing, so a developer without the key (or a
// preview build) still gets a working app. The backend runs App Check in
// monitor mode for exactly this reason - see functions/config/appCheck.js.
let appCheck = null;
const recaptchaSiteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

if (recaptchaSiteKey) {
  try {
    // Must be set BEFORE initializeAppCheck. On localhost this makes the SDK
    // print a debug token to the console, which you register under
    // App Check -> Apps -> Manage debug tokens to let local dev through
    // without a real reCAPTCHA assessment.
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-restricted-globals
      self.FIREBASE_APPCHECK_DEBUG_TOKEN =
        process.env.REACT_APP_APPCHECK_DEBUG_TOKEN || true;
    }

    appCheck = initializeAppCheck(firebaseApp, {
      provider: new ReCaptchaV3Provider(recaptchaSiteKey),
      // Keeps the token fresh in the background so long-lived tabs (the admin
      // dashboard is left open all day) don't start failing mid-session.
      isTokenAutoRefreshEnabled: true
    });
  } catch (error) {
    // A bad key or a blocked reCAPTCHA script must not take the whole app
    // down - the backend decides whether a missing token is fatal.
    console.warn('App Check could not be initialized:', error);
    appCheck = null;
  }
} else if (process.env.NODE_ENV === 'production') {
  console.warn(
    'REACT_APP_RECAPTCHA_SITE_KEY is not set - this build sends no App Check tokens.'
  );
}

// Initialize services
const auth = getAuth(firebaseApp);

// Initialize Firestore with the "wafflestop" database
const db = getFirestore(firebaseApp, 'wafflestop');

// Try to enable IndexedDB persistence for offline capability
try {
  enableIndexedDbPersistence(db).catch((err) => {
    if (err.code === 'failed-precondition') {
      console.warn(
        'Firebase persistence could not be enabled: multiple tabs open'
      );
    } else if (err.code === 'unimplemented') {
      console.warn('Firebase persistence not supported in this browser');
    } else {
      console.error('Unknown error enabling Firebase persistence:', err);
    }
  });
} catch (error) {
  console.warn('Error trying to enable persistence:', error);
}

/**
 * Current App Check token for our own REST API, or null.
 *
 * Returns null rather than throwing on any failure (App Check not configured,
 * reCAPTCHA blocked by an extension, offline). The request then goes out
 * without the header and the backend decides what to do about it - which is
 * nothing at all while it's in monitor mode. Failing the request here instead
 * would turn an App Check hiccup into a broken storefront.
 */
export const getAppCheckToken = async () => {
  if (!appCheck) return null;

  try {
    // Imported lazily so the app-check chunk isn't pulled in for builds that
    // never configure a site key.
    const { getToken } = await import('firebase/app-check');
    const result = await getToken(appCheck, /* forceRefresh */ false);
    return result?.token || null;
  } catch (error) {
    console.warn('Could not get App Check token:', error);
    return null;
  }
};

export { auth, db, firebaseApp, appCheck };
